import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  editNotice,
  fetchAllNotices,
  publishNotice,
  retireNotice,
  writeNotice,
  type AdminNotice,
  type NoticeDraft,
  type NoticeTone,
} from '../api/notices'
import ErrorNote from './ErrorNote'

/**
 * Writing what the studio is telling people today (D-790).
 *
 * <b>The screen exists so a defect can be announced without a deploy.</b> A
 * what's-new entry ships inside the build, so using it for a live fault needs
 * the very deploy you are trying to avoid — and a deploy restarts the site
 * under real families (D-33).
 *
 * <b>Beside Feedback and Website in the rail</b>, because it is the same kind
 * of thing: a studio-level switch rather than anything about a class or a
 * person.
 */
export default function NoticesView() {
  const client = useQueryClient()
  const [writing, setWriting] = useState<AdminNotice | 'new' | null>(null)

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['admin-notices'],
    queryFn: fetchAllNotices,
  })

  const done = () => {
    setWriting(null)
    client.invalidateQueries({ queryKey: ['admin-notices'] })
    // The banner reads its own list, and checking your own work has to show
    // your own work (D-210's instinct).
    client.invalidateQueries({ queryKey: ['notices'] })
  }

  const publish = useMutation({ mutationFn: publishNotice, onSuccess: done })
  const retire = useMutation({ mutationFn: retireNotice, onSuccess: done })

  const notices = data ?? []
  const live = notices.filter((n) => n.live)
  const rest = notices.filter((n) => !n.live)

  return (
    <div className="page-shell px-5 py-8">
      <header className="mb-5">
        <p className="text-[11px] font-bold tracking-[0.14em] text-accent uppercase">Admin</p>
        <h1 className="font-display text-2xl">Notices</h1>
        <p className="mt-1.5 max-w-[44rem] text-[13.5px] text-ink-3">
          A banner at the top of the app for the people you choose. Use it to say a fault is known
          and what to do meanwhile &mdash; it goes up straight away, with no new version of the app.
        </p>
      </header>

      {isError && <ErrorNote boxed>{(error as Error).message}</ErrorNote>}

      <button
        type="button"
        onClick={() => setWriting('new')}
        className="mb-5 rounded-lg border border-accent px-3 py-2 text-[13px] font-semibold text-accent-2"
      >
        Write a notice
      </button>

      {writing && (
        <NoticeForm
          notice={writing === 'new' ? null : writing}
          onCancel={() => setWriting(null)}
          onSaved={done}
        />
      )}

      {isPending && <p className="text-sm text-ink-3">Loading...</p>}

      {!isPending && notices.length === 0 && (
        <p className="rounded-xl border border-line bg-surface px-4 py-6 text-center text-[13px] text-ink-3">
          Nothing has been announced. That is the ordinary state.
        </p>
      )}

      {live.length > 0 && (
        <Section title="Showing now">
          {live.map((n) => (
            <Row
              key={n.id}
              notice={n}
              onEdit={() => setWriting(n)}
              action={{ label: 'Retire', run: () => retire.mutate(n.id) }}
            />
          ))}
        </Section>
      )}

      {rest.length > 0 && (
        <Section title="Not showing">
          {rest.map((n) => (
            <Row
              key={n.id}
              notice={n}
              onEdit={() => setWriting(n)}
              action={
                n.retiredAt
                  ? undefined
                  : { label: 'Publish', run: () => publish.mutate(n.id) }
              }
            />
          ))}
        </Section>
      )}
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-6">
      <h2 className="mb-2 text-[11px] font-bold tracking-[0.12em] text-ink-3 uppercase">{title}</h2>
      <ul className="flex flex-col gap-2">{children}</ul>
    </section>
  )
}

function Row({
  notice,
  onEdit,
  action,
}: {
  notice: AdminNotice
  onEdit: () => void
  action?: { label: string; run: () => void }
}) {
  return (
    <li className="rounded-xl border border-line bg-surface px-4 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-[13.5px] font-semibold wrap-anywhere">{notice.headline}</span>

        <span className="flex shrink-0 gap-2">
          <button type="button" onClick={onEdit} className="text-[12.5px] font-semibold text-accent-2">
            Edit
          </button>
          {action && (
            <button type="button" onClick={action.run} className="text-[12.5px] font-semibold text-warn">
              {action.label}
            </button>
          )}
        </span>
      </div>

      <p className="mt-1 text-[12.5px] text-ink-2 wrap-anywhere">{notice.body}</p>

      <p className="mt-1.5 flex flex-wrap gap-x-3 text-[11.5px] text-ink-3">
        <span>For {notice.for.join(', ')}</span>
        <span>{notice.tone === 'Issue' ? 'A problem' : 'Information'}</span>
        <span>{notice.dismissible ? 'Can be dismissed' : 'Stays put'}</span>
        {notice.retiredAt && <span>Retired</span>}
      </p>
    </li>
  )
}

/**
 * Writing one, or changing one already written.
 */
function NoticeForm({
  notice,
  onCancel,
  onSaved,
}: {
  notice: AdminNotice | null
  onCancel: () => void
  onSaved: () => void
}) {
  const [headline, setHeadline] = useState(notice?.headline ?? '')
  const [body, setBody] = useState(notice?.body ?? '')
  const [forWhom, setForWhom] = useState<string[]>(notice?.for ?? ['staff'])
  const [tone, setTone] = useState<NoticeTone>(notice?.tone ?? 'Issue')
  const [dismissible, setDismissible] = useState(notice?.dismissible ?? true)

  const save = useMutation({
    mutationFn: (draft: NoticeDraft) =>
      notice ? editNotice(notice.id, draft) : writeNotice(draft),
    onSuccess: onSaved,
  })

  const toggle = (who: string) =>
    setForWhom((current) =>
      current.includes(who) ? current.filter((w) => w !== who) : [...current, who],
    )

  return (
    <form
      className="mb-6 flex flex-col gap-4 rounded-2xl border border-line bg-surface p-5"
      onSubmit={(e) => {
        e.preventDefault()
        save.mutate({ headline, body, for: forWhom, tone, dismissible })
      }}
    >
      <label className="flex flex-col gap-1 text-sm">
        <span className="font-semibold text-ink-2">Headline</span>
        <input
          value={headline}
          maxLength={120}
          onChange={(e) => setHeadline(e.target.value)}
          placeholder="Taking attendance from Home opens the wrong page"
          className="w-full rounded-lg border border-line px-3 py-2"
        />
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="font-semibold text-ink-2">What to do</span>
        <textarea
          value={body}
          maxLength={2000}
          rows={3}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Go to Schedule and tap your class instead. A fix is coming."
          className="w-full rounded-lg border border-line px-3 py-2"
        />
        <span className="text-xs text-ink-3">
          The workaround, in the words you would use to say it out loud.
        </span>
      </label>

      <div className="flex flex-col gap-1 text-sm">
        <span className="font-semibold text-ink-2">Who sees it</span>
        <div className="flex flex-wrap gap-2">
          {[
            ['staff', 'Staff'],
            ['parent', 'Parents'],
            ['dancer', 'Dancers'],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={forWhom.includes(value)}
              onClick={() => toggle(value)}
              className={`min-h-[2.25rem] rounded-lg border px-3 py-1.5 text-[13px] font-semibold ${
                forWhom.includes(value)
                  ? 'border-accent bg-accent-soft text-accent-2'
                  : 'border-line-2 bg-surface text-ink-2 hover:border-ink-3'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-1 text-sm">
        <span className="font-semibold text-ink-2">How it reads</span>
        <div className="flex flex-wrap gap-2">
          {([
            ['Issue', 'A problem'],
            ['Info', 'Information'],
          ] as const).map(([value, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={tone === value}
              onClick={() => setTone(value)}
              className={`min-h-[2.25rem] rounded-lg border px-3 py-1.5 text-[13px] font-semibold ${
                tone === value
                  ? 'border-accent bg-accent-soft text-accent-2'
                  : 'border-line-2 bg-surface text-ink-2 hover:border-ink-3'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          checked={dismissible}
          onChange={(e) => setDismissible(e.target.checked)}
          className="mt-1"
        />
        <span>
          <span className="font-semibold text-ink-2">They can dismiss it</span>
          <span className="mt-0.5 block text-xs text-ink-3">
            Leave this on for most things. Turn it off for something that should stay in front of
            people until it is over.
          </span>
        </span>
      </label>

      {save.isError && (
        <p role="alert" className="text-sm text-warn">
          {(save.error as Error).message}
        </p>
      )}

      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" onClick={onCancel} className="rounded-lg px-4 py-2 text-sm font-semibold text-ink-2">
          Cancel
        </button>
        <button
          type="submit"
          disabled={save.isPending}
          className="rounded-lg border border-accent bg-accent px-4 py-2 text-sm font-semibold text-on-accent disabled:opacity-60"
        >
          {notice ? 'Save' : 'Save as draft'}
        </button>
      </div>

      {!notice && (
        <p className="text-right text-xs text-ink-3">
          Saving writes it down. It shows to nobody until you publish it.
        </p>
      )}
    </form>
  )
}
